import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { NavGroupItem } from './NavGroupItem.js';
import { NavLeafItem } from './NavLeafItem.js';
import { useSpotlightedNavSection } from './navSpotlight.js';
import { navSectionAnchorId } from './navTourAnchors.js';
import type { NavChild, NavSection } from './types.js';

export function NavSectionItem({
  section,
  collapsed,
  onNavigate,
  isPopoverOpen,
  popoverAnchor,
  requestOpen,
  scheduleClose,
  cancelClose,
  closePopover,
}: {
  section: NavSection;
  collapsed: boolean;
  onNavigate?: () => void;
  isPopoverOpen: boolean;
  popoverAnchor: HTMLElement | null;
  requestOpen: (id: string, anchor: HTMLElement) => void;
  scheduleClose: () => void;
  cancelClose: () => void;
  closePopover: () => void;
}) {
  const { pathname } = useLocation();
  const isActive = section.isActive?.(pathname) ?? pathname.startsWith(section.href);

  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  // An onboarding tour pointing at one of this section's entries keeps it open for as long as it
  // runs, overriding both the manual toggle and the active-route default.
  const isSpotlighted = useSpotlightedNavSection() === section.id;
  const expanded = isSpotlighted || (userExpanded ?? isActive);

  // On navigation, drop any manual toggle so only the section containing the
  // active route stays open (Minimal-MUI sidebar behavior).
  useEffect(() => {
    setUserExpanded(null);
  }, [pathname]);

  const handleTriggerMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!collapsed) return;
      requestOpen(section.id, e.currentTarget);
    },
    [collapsed, requestOpen, section.id]
  );

  const handleTriggerMouseLeave = useCallback(() => {
    if (!collapsed) return;
    scheduleClose();
  }, [collapsed, scheduleClose]);

  const handleLeafClick = useCallback(
    (inPopover: boolean) => {
      if (inPopover) closePopover();
      onNavigate?.();
    },
    [closePopover, onNavigate]
  );

  const renderChildren = useCallback(
    (children: NavChild[], inPopover = false) => {
      const onLeafClick = inPopover || onNavigate ? () => handleLeafClick(inPopover) : undefined;
      return children.map((child, i) => {
        const isLast = i === children.length - 1;
        const lineMode = inPopover ? undefined : isLast ? 'segment-half' : 'segment-full';
        return child.kind === 'leaf' ? (
          <NavLeafItem
            key={child.href}
            indented={!inPopover}
            item={child}
            lineMode={lineMode}
            onClose={onLeafClick}
          />
        ) : (
          <NavGroupItem
            key={child.label}
            group={child}
            inPopover={inPopover}
            isLastInSection={!inPopover && isLast}
            showTopDivider={inPopover && i > 0}
            onClose={onLeafClick}
          />
        );
      });
    },
    [handleLeafClick, onNavigate]
  );

  return (
    <>
      <ListItemButton
        id={navSectionAnchorId(section.id)}
        selected={isActive}
        sx={(theme) => ({
          borderRadius: 1,
          minHeight: collapsed ? 56 : 44,
          mx: 0.5,
          mb: 0.5,
          px: collapsed ? 1.5 : 1,
          flexDirection: collapsed ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: collapsed ? 0.25 : 1,
          color: alpha(theme.palette.common.white, 0.7),
          transition: 'none',
          ...(!collapsed &&
            expanded && {
              bgcolor: alpha(theme.palette.common.white, 0.08),
              color: theme.palette.common.white,
            }),
          '&:hover': {
            bgcolor: alpha(theme.palette.common.white, 0.08),
            color: theme.palette.common.white,
          },
          '&.Mui-selected': {
            bgcolor: alpha(theme.palette.secondary.main, 0.13),
            color: theme.palette.secondary.main,
            '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
          },
        })}
        onClick={collapsed ? undefined : () => setUserExpanded((v) => !(v ?? isActive))}
        onMouseEnter={handleTriggerMouseEnter}
        onMouseLeave={handleTriggerMouseLeave}
      >
        <ListItemIcon sx={{ minWidth: 0, color: 'inherit', justifyContent: 'center' }}>
          {section.icon}
        </ListItemIcon>
        {collapsed ? (
          <Typography
            sx={{
              fontSize: '0.6rem',
              fontWeight: 500,
              lineHeight: 1.2,
              textAlign: 'center',
              color: 'inherit',
              maxWidth: 58,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {section.label}
          </Typography>
        ) : (
          <>
            <ListItemText
              primary={section.label}
              slotProps={{ primary: { variant: 'body2', fontWeight: isActive ? 600 : 400 } }}
              sx={{ m: 0 }}
            />
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 18, opacity: 0.6 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18, opacity: 0.6 }} />
            )}
          </>
        )}
      </ListItemButton>

      {!collapsed && (
        <Collapse unmountOnExit in={expanded} timeout="auto">
          <Box
            sx={{
              position: 'relative',
              ml: '28px',
              mr: 1,
              pl: '12px',
              mb: 1,
            }}
          >
            <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column' }}>
              {renderChildren(section.children)}
            </List>
          </Box>
        </Collapse>
      )}

      {collapsed && (
        <Popper
          anchorEl={popoverAnchor}
          modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
          open={isPopoverOpen && Boolean(popoverAnchor)}
          placement="right-start"
          style={{ zIndex: 1300 }}
        >
          <Paper
            sx={(theme) => ({
              bgcolor: 'grey.800',
              color: theme.palette.common.white,
              minWidth: 210,
              boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.45)}`,
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
              py: 1,
            })}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <List dense disablePadding>
              {renderChildren(section.children, true)}
            </List>
          </Paper>
        </Popper>
      )}
    </>
  );
}
